import { $, $$, el } from './dom.js';
import { api } from './api.js';
import { toastError } from './toast.js';
import { ct } from './i18n.js';

/**
 * The live preview of a generated card, and the presets beside it.
 *
 * Written once and mounted per card: the Levels page has one, the Welcome page
 * has two, and each keeps its own values. Sharing the *code* is what stops the
 * three drifting apart; sharing the *values* was the mistake this module exists
 * to have undone.
 *
 * Requests are debounced and the previous one is aborted. Dragging an opacity
 * slider fires a change per pixel, and every one of them can fetch a remote
 * background - without this, the dashboard would flood both our server and
 * somebody else's.
 */

/**
 * English fallbacks for the background warnings, keyed as the server sends
 * them (`backgroundWarningKey`): in the preview header and in the warnings a
 * save returns. `ct` needs one per key so a key forgotten in a dictionary never
 * renders an empty toast; the real sentences live in the sixteen dictionaries.
 */
export const CARD_BACKGROUND_WARNINGS: Record<string, string> = {
  cardBackgroundRefused:
    'The host of the background image refuses to send it to our server, so the card shows the plain colour. Host it elsewhere (ImgBB, Postimages) and paste the direct link.',
  cardBackgroundNotFound:
    'The background image was not found at that address: the card shows the plain colour. Check the link.',
  cardBackgroundNotImage:
    'The background link does not lead to an image file. Use the direct link (ending in .png, .jpg…), not the page that shows it.',
  cardBackgroundTooLarge:
    'The background image is over 8 MB: the card shows the plain colour. Use a lighter image.',
  cardBackgroundUnreachable:
    'The background image could not be downloaded (address unreachable or too slow): the card shows the plain colour.',
};

export interface CardAppearanceValues {
  font: string;
  layout: string;
  avatarShape: string;
  overlayOpacity: number;
  accentColor: string;
  titleColor: string;
  nameColor: string;
  textColor: string;
  overlayColor: string;
  backgroundColor: string;
  backgroundImageUrl: string;
}

/**
 * Backgrounds offered by the "surprise me" button and the swatches.
 *
 * Gradient-free flat sets rather than photographs: they weigh nothing, need no
 * hosting, and cannot rot the way a hotlinked image does. A server wanting a
 * picture types its address in the field just above.
 */
const GALLERY = [
  { name: 'Minuit', background: '#0f172a', overlay: '#0f172a', accent: '#5865f2' },
  { name: 'Océan', background: '#082f49', overlay: '#0c4a6e', accent: '#38bdf8' },
  { name: 'Forêt', background: '#052e16', overlay: '#14532d', accent: '#22c55e' },
  { name: 'Braise', background: '#431407', overlay: '#7c2d12', accent: '#fb923c' },
  { name: 'Prune', background: '#2e1065', overlay: '#4c1d95', accent: '#c084fc' },
  { name: 'Ardoise', background: '#1c1917', overlay: '#292524', accent: '#e7e5e4' },
];

export interface CardPreviewOptions {
  /** The form the controls live in. */
  form: HTMLFormElement;
  /** Value of the `data-card-preview` attribute identifying this card. */
  name: string;
  /** Endpoint that renders it, already carrying the guild. */
  endpoint: string;
  /**
   * Builds the request body.
   *
   * A body rather than the appearance alone: each endpoint wants its own shape
   * - the Levels one takes `{ guildId, card }`, the Welcome one also needs to
   * know which block it is drawing.
   */
  read: () => unknown;
}

/** Mounts the preview, the gallery swatches and the random button. */
export function mountCardPreview(options: CardPreviewOptions): void {
  const { form, name } = options;

  const scope = $(`[data-card-appearance="${name}"]`, form);
  const image = $<HTMLImageElement>(`[data-card-preview="${name}"]`, form);
  if (!scope || !image) return;

  let timer: number | undefined;
  let inFlight: AbortController | undefined;
  let objectUrl: string | undefined;

  /*
   * The last payload actually sent.
   *
   * Typing a word and deleting it, dragging a slider back to where it started,
   * opening a colour picker and closing it unchanged — each fires `input` and
   * each used to cost a rendered PNG. The picture would have been identical;
   * the only thing spent was the ceiling that protects the route.
   */
  let lastSent = '';

  /** The background warning last shown, so a redraw does not repeat it. */
  let lastBackgroundWarning: string | null = null;

  async function refresh(): Promise<void> {
    if (!image) return;

    const payload = options.read();
    const signature = JSON.stringify(payload);
    if (signature === lastSent) return;

    inFlight?.abort();
    const controller = new AbortController();
    inFlight = controller;

    try {
      lastSent = signature;
      const { blob, headers } = await api.postBlob(options.endpoint, payload, controller.signal);

      // The previous object URL is released before the new one replaces it:
      // without this, every keystroke leaks a blob for the life of the page.
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      objectUrl = URL.createObjectURL(blob);
      image.src = objectUrl;
      image.removeAttribute('data-preview-error');

      /*
       * Said once per failure, not once per redraw: the preview refreshes on
       * every colour change, and the same toast at each one would bury the
       * page. Cleared when the background loads, so a link that breaks again
       * later is said again.
       */
      const background = headers.get('x-card-background');
      if (background && background !== lastBackgroundWarning) {
        toastError(ct(background, CARD_BACKGROUND_WARNINGS[background] ?? background));
      }
      lastBackgroundWarning = background;
    } catch (error) {
      /*
       * An abort is the ordinary case; anything else is a fault worth saying.
       *
       * The first version swallowed both. So a background image the server
       * refused — an address it could not reach, a rate limit reached while
       * typing, a URL the schema rejected — left the previous picture on
       * screen and no explanation anywhere. The setting looked ignored, when
       * it had in fact been rejected for a reason nobody was told.
       */
      if (error instanceof Error && error.name === 'AbortError') return;

      /*
       * Forgotten on failure, so the next attempt is not skipped as a
       * duplicate. Remembering a payload that produced no picture would leave
       * the card wrong until something else changed.
       */
      lastSent = '';

      image.dataset.previewError = 'true';
      toastError(error instanceof Error ? error.message : String(error));
    }
  }

  function schedule(): void {
    window.clearTimeout(timer);
    /*
     * Four hundred milliseconds, not two hundred and fifty.
     *
     * The route allows twenty renders a minute — it fetches remote background
     * images, and without a ceiling a held-down colour picker turns the
     * dashboard into a proxy hammering somebody else's server. At 250 ms a
     * sustained drag asks for two hundred and forty a minute: the page was
     * built to exceed its own ceiling twelvefold, and did, four changes in.
     */
    timer = window.setTimeout(() => void refresh(), 400);
  }

  /*
   * Listening on the form rather than on the panel: the select menus are
   * replaced by a custom component whose events fire from a sibling element,
   * so a listener bound inside the panel would miss half the changes.
   */
  for (const event of ['input', 'change'] as const) {
    form.addEventListener(event, (raw) => {
      if (belongsTo(raw.target, scope, name)) schedule();
    });
  }

  mountGallery(form, scope, name, schedule);

  void refresh();
}

/**
 * Whether a control drives *this* card rather than a neighbouring one.
 *
 * The block that owns the card is wider than the appearance panel: the title,
 * the subtitle and the free line live beside it, under `data-card-config`. They
 * were outside the scope, so typing a new card title changed nothing on screen
 * — the preview only caught up after a save and a reload, which reads as a
 * broken preview rather than as a scope that is one element too narrow.
 */
function belongsTo(target: EventTarget | null, scope: Element, name: string): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (scope.contains(target)) return true;

  const block = target.closest<HTMLElement>('[data-card-config]');
  if (block?.dataset.cardConfig === name) return true;

  // The custom select mounts its trigger outside the original element; it
  // carries the name of the field it replaced.
  const owner = target.closest<HTMLElement>('[data-select-menu-for]');
  return Boolean(owner?.dataset.selectMenuFor?.includes(name));
}

/** Colour presets, and the button that picks one at random. */
function mountGallery(
  form: HTMLFormElement,
  scope: Element,
  name: string,
  onChange: () => void,
): void {
  const container = $(`[data-card-gallery="${name}"]`, form);

  const apply = (preset: (typeof GALLERY)[number]): void => {
    setColor(scope, 'backgroundColor', preset.background);
    setColor(scope, 'overlayColor', preset.overlay);
    setColor(scope, 'accentColor', preset.accent);
    onChange();
    form.dispatchEvent(new Event('change', { bubbles: true }));
  };

  if (container) {
    for (const preset of GALLERY) {
      const swatch = el('button', {
        class: 'btn btn-ghost btn-xs gap-2',
        text: preset.name,
        attrs: { type: 'button' },
      });

      const dot = el('span', { class: 'inline-block size-3 rounded-full' });
      // Inline styles are blocked by the CSP: the colour goes through the CSSOM.
      dot.style.backgroundColor = preset.accent;
      swatch.prepend(dot);

      swatch.addEventListener('click', () => {
        apply(preset);
      });
      container.append(swatch);
    }
  }

  $(`[data-card-random="${name}"]`, form)?.addEventListener('click', () => {
    const preset = GALLERY[Math.floor(Math.random() * GALLERY.length)];
    if (preset) apply(preset);
  });
}

/**
 * Sets one colour input inside a card panel, by the suffix of its name.
 *
 * Selected by `data-color-input`, not by `type="color"`.
 *
 * `upgradeColorInputs` replaces every native colour field with a custom picker
 * and sets the original to `type="hidden"` — so by the time anybody clicks
 * "Surprise me", `input[type="color"]` matches nothing. The button set no
 * colour, redrew the same card, and looked broken while working exactly as
 * written. The data attribute survives the upgrade; the type does not.
 *
 * The `input` event is dispatched because the swatch beside the field draws
 * itself from what it last heard. Setting `.value` alone repaints the card and
 * leaves the picker showing the previous colour — two truths on one screen.
 */
function setColor(scope: Element, suffix: string, value: string): void {
  const field = $$<HTMLInputElement>('input[data-color-input]', scope).find((input) =>
    input.name.endsWith(`.${suffix}`),
  );
  if (!field) return;

  field.value = value;
  field.dispatchEvent(new Event('input', { bubbles: true }));
}

/** Reads the appearance out of one card panel, whatever its field prefix is. */
export function readAppearance(form: HTMLFormElement, name: string): CardAppearanceValues {
  const scope = $(`[data-card-appearance="${name}"]`, form);

  const value = (suffix: string, fallback: string): string => {
    if (!scope) return fallback;
    const field = $$<HTMLInputElement | HTMLSelectElement>('input, select', scope).find((input) =>
      input.name.endsWith(`.${suffix}`),
    );
    return field?.value || fallback;
  };

  return {
    font: value('font', 'Montserrat'),
    layout: value('layout', 'classic'),
    avatarShape: value('avatarShape', 'circle'),
    overlayOpacity: Number(value('overlayOpacity', '70')),
    accentColor: value('accentColor', '#5865f2'),
    titleColor: value('titleColor', '#ffffff'),
    nameColor: value('nameColor', '#ffffff'),
    textColor: value('textColor', '#ffffff'),
    overlayColor: value('overlayColor', '#0f172a'),
    backgroundColor: value('backgroundColor', '#1e293b'),
    backgroundImageUrl: value('backgroundImageUrl', ''),
  };
}
